import { TEvent, TPassOnAccount, TPassReservationLimit } from "src/shared/types/globalStotre.types";
import { TEventAttendance } from "./types.event-attendance";

export function updateEventsWithAttendance(args: {
  events: TEvent[];
  attendance: TEventAttendance;
  isReservationEnabled?: boolean;
}) {
  const { events, attendance, isReservationEnabled } = args;
  const stateEvents = [...events];
  const eventsUpdated = stateEvents.map((evt) => {
    if (evt.eventCode === attendance.event.eventCode) {
      // Secondary confirmation update
      if (attendance.isSecondaryConfirmation) {
        evt.secondaryOptIn = attendance.optInResponse;
        if (!attendance.optInResponse) {
          evt.optedIn = false;
          evt.secondaryOptIn = null;
          if (isReservationEnabled) evt.optedInSeatCount = 0;
        }
        return evt;
      }

      evt.optedIn = attendance.optInResponse;
      if (isReservationEnabled) {
        const seatCount = attendance.optInResponse ? attendance.seatCount : 0;
        evt.optedInSeatCount = seatCount;
      }
    }
    return evt;
  });
  return eventsUpdated;
}

/**
 * Edge-case fallback: when a user somehow ends up holding multiple
 * month-passes simultaneously, pick the one whose coverage period most
 * likely includes today instead of taking min/max/sum across all of them.
 *
 * Heuristic: prefer the pass with the latest `coverageStartDate <= now`.
 * If no pass has a coverageStartDate (e.g., user only holds a head pass),
 * or all coverageStartDates are in the future, fall back to the original
 * input array unchanged so callers can apply their previous min/max logic.
 */
export function pickCurrentMonthPass(
  passesOnAccount: TPassOnAccount[]
): TPassOnAccount | null {
  if (!passesOnAccount?.length) return null;
  if (passesOnAccount.length === 1) return passesOnAccount[0];

  const now = Date.now();
  const candidates = passesOnAccount
    .filter((pass) => pass?.coverageStartDate)
    .map((pass) => ({
      pass,
      startMs: new Date(pass.coverageStartDate as string).getTime(),
    }))
    .filter(({ startMs }) => Number.isFinite(startMs) && startMs <= now)
    .sort((a, b) => b.startMs - a.startMs); // latest start first

  return candidates[0]?.pass ?? null;
}

/**
 * Gets the minimum activeReservationAllowed value from passes array using reducer
 * @param passesOnAccount Array of passes with activeReservationAllowed values
 * @returns minimum activeReservationAllowed number or null if none found
 */
export function calcMinReservationAllowed(passesOnAccount: TPassOnAccount[]) {
  const currentPass = pickCurrentMonthPass(passesOnAccount);
  if (currentPass?.activeReservationAllowed != null) {
    return currentPass.activeReservationAllowed;
  }
  return Math.min(
    ...passesOnAccount.map((pass) => {
      if (!pass?.activeReservationAllowed) return Infinity;
      return pass?.activeReservationAllowed;
    })
  );
}

/**
 * Gets the reservationsAllowed value scoped to the user's current month-pass.
 * When the user holds a single pass (the standard case) this is just that
 * pass's cap. When they somehow hold multiple, picks the cap from the pass
 * whose coverage period includes today rather than min/max/sum across all.
 * Falls back to activeReservationAllowed if reservationsAllowed is null.
 */
export function calcMinReservationsAllowed(passesOnAccount: TPassOnAccount[]) {
  const currentPass = pickCurrentMonthPass(passesOnAccount);
  if (currentPass?.reservationsAllowed != null) {
    return currentPass.reservationsAllowed;
  }

  const minReservationsAllowed = Math.min(
    ...passesOnAccount.map((pass) => {
      if (pass?.reservationsAllowed == null) return Infinity;
      return pass.reservationsAllowed;
    })
  );
  if (minReservationsAllowed === Infinity) return calcMinReservationAllowed(passesOnAccount);
  return minReservationsAllowed;
}

/**
 * Checks if active reservation restrictions should disable attendance buttons
 * Uses reservationsAllowed + optedInCountForEventReservation when available,
 * otherwise falls back to activeReservationAllowed + local opted-in count.
 *
 * When the BE resolves the limit per pass (event.isReservationLimitReached is
 * set — teams with scopeReservationLimitByPass), that value wins: it applies
 * both maxReservationsAllowed (incl. past games) and
 * maxActiveReservationsAllowed for the passes mapped to this event, the same
 * rule the BE enforces on reserve.
 */
// prettier-ignore
export function isReservationLimitReached(args: {
  passesOnAccount: TPassOnAccount[];
  events: TEvent[];
  event: TEvent;
  optedInCountForEventReservation?: number | null;
  useOptedInCountForEventReservation?: boolean;
}): boolean {
  const { passesOnAccount, events, event, optedInCountForEventReservation, useOptedInCountForEventReservation } = args;
  const isOptedIn = Boolean(event?.optedIn);

  if (typeof event?.isReservationLimitReached === "boolean") {
    return event.isReservationLimitReached && !isOptedIn;
  }

  const reservationsAllowed = calcMinReservationsAllowed(passesOnAccount);
  let optedInCount = events.filter((ev) => ev?.optedIn === true)?.length || 0;

  if (useOptedInCountForEventReservation && optedInCountForEventReservation && optedInCountForEventReservation != null) {
    optedInCount = optedInCountForEventReservation;
  }

  const isMaxedOut = optedInCount >= reservationsAllowed;
  return isMaxedOut && !isOptedIn;
}

/**
 * Reservations the user can exchange away to reserve `newEvent`: free and no
 * guest tickets. When the BE resolves limits per pass, only reservations on a
 * pass mapped to `newEvent` count (swapping a Nov reservation must not free up
 * an Oct slot) — the BE rejects cross-pass exchanges too.
 */
// prettier-ignore
export function getExchangeEligibleReservations(args: {
  events: TEvent[];
  newEvent: TEvent | null | undefined;
}): TEvent[] {
  const { events, newEvent } = args;
  const eligible = (events ?? []).filter(
    (e) => e.optedIn === true && !e.isPaymentRequired && !((e.seatInformation?.GUEST ?? 0) > 0)
  );
  if (typeof newEvent?.isReservationLimitReached !== "boolean") return eligible;

  const newEventPassIds = newEvent.userPassIds ?? [];
  return eligible.filter((e) => (e.userPassIds ?? []).some((passId) => newEventPassIds.includes(passId)));
}

/**
 * Totals for the "Reservations Available" tile. Uses the BE's per-pass usage
 * when present (sums each pass's limit and remaining — e.g. Oct pass + Nov
 * pass), otherwise one pooled limit across every pass on account.
 */
// prettier-ignore
export function calcReservationAvailability(args: {
  passesOnAccount: TPassOnAccount[];
  events: TEvent[];
  optedInCountForEventReservation?: number | null;
  useOptedInCountForEventReservation?: boolean;
  reservationLimitsByPass?: TPassReservationLimit[] | null;
}): { reservationLimit: number; remainingReservation: number } {
  const { passesOnAccount, events, optedInCountForEventReservation, useOptedInCountForEventReservation, reservationLimitsByPass } = args;

  if (reservationLimitsByPass?.length) {
    return reservationLimitsByPass.reduce(
      (totals, pass) => ({
        reservationLimit: totals.reservationLimit + pass.reservationLimit,
        remainingReservation: totals.remainingReservation + pass.remaining,
      }),
      { reservationLimit: 0, remainingReservation: 0 }
    );
  }

  const reservationLimit = calcMinReservationsAllowed(passesOnAccount);
  const localOptedInCount = events.filter((ev) => ev?.optedIn === true)?.length || 0;
  const alreadyReserved =
    useOptedInCountForEventReservation && optedInCountForEventReservation != null
      ? optedInCountForEventReservation
      : localOptedInCount;
  return { reservationLimit, remainingReservation: Math.max(reservationLimit - alreadyReserved, 0) };
}

export function shouldShowExchangeButton(args: {
  event: TEvent;
  passesOnAccount: TPassOnAccount[];
  events: TEvent[];
  isReservationEnabled: boolean;
  isExchangeEnabled?: boolean;
  optedInCountForEventReservation?: number | null;
  useOptedInCountForEventReservation?: boolean;
}): boolean {
  const { event, passesOnAccount, events, isReservationEnabled, isExchangeEnabled, optedInCountForEventReservation, useOptedInCountForEventReservation } = args;
  if (!isExchangeEnabled) return false; // exchange flow is opt-in per team
  if (!isReservationEnabled) return false;
  if (Boolean(event.optedIn)) return false; // already reserved — show the "Reserved" button instead
  if (!event.isOptInEnabled) return false; //for event with NULL Opt in dates, we still need to check OptIn is enable for this event
  if (isEventMaxSeatLimitReached(event)) return false; // event is full — nothing to exchange into
  if (!isReservationLimitReached({ passesOnAccount, events, event, optedInCountForEventReservation, useOptedInCountForEventReservation })) return false;
  return getExchangeEligibleReservations({ events, newEvent: event }).length > 0;
}

export function calcEventRemainingSeatCount(event: TEvent) {
  const maxTotalClaimCount =
    typeof event?.maxTotalClaimCount == "number"
      ? Number(event?.maxTotalClaimCount)
      : Infinity;
  const currentTotalClaimCount = Number(event?.currentTotalClaimCount) || 0;
  const remainingSeatCount = maxTotalClaimCount - currentTotalClaimCount;
  if (remainingSeatCount <= 0) return 0;
  return remainingSeatCount;
}

/**
 * The TM-inventory reservation cap for this event, scoped to the user's single
 * active pass (the BE resolves it via the pass effective-date window and sends
 * one value). Returns undefined when no cap is synced — callers treat undefined
 * as "no cap".
 */
export function getReservationSeatCap(event: TEvent): number | undefined {
  return event?.totalAvailableSeatsForReservation ?? undefined;
}

export function isEventMaxSeatLimitReached(event: TEvent): boolean {
  if (calcEventRemainingSeatCount(event) <= 0) return true;
  const cap = getReservationSeatCap(event);
  return cap != null && cap <= 0;
}

// --- Secondary Confirmation Utilities ---

export type TSecondaryConfirmationStatus =
  | "not_applicable"
  | "awaiting_window"
  | "window_open"
  | "confirmed"
  | "missed";

export function hasSecondaryConfirmation(event: TEvent): boolean {
  return Boolean(event?.secondaryStartDateTime);
}

export function getSecondaryConfirmationStatus(
  event: TEvent
): TSecondaryConfirmationStatus {
  if (!hasSecondaryConfirmation(event)) return "not_applicable";

  // If already confirmed via secondary, it's done
  if (event.secondaryOptIn === true) return "confirmed";

  // User hasn't opted in yet at primary level — secondary is not relevant yet
  if (!event.optedIn) return "not_applicable";

  // When the backend provides isSecondaryOptInEnabled, use it directly
  // (the backend already handles timezone-aware date window calculations)
  if (event.isSecondaryOptInEnabled != null) {
    if (event.isSecondaryOptInEnabled) return "window_open";
    // Window is closed and user didn't confirm — determine if it hasn't opened or already passed
    const now = new Date();
    const secondaryStart = new Date(event.secondaryStartDateTime!);
    if (now < secondaryStart) return "awaiting_window";
    return "missed";
  }

  // Fallback for older API versions without isSecondaryOptInEnabled
  const now = new Date();
  const secondaryStart = new Date(event.secondaryStartDateTime!);
  const secondaryEnd = event.secondaryEndDateTime
    ? new Date(event.secondaryEndDateTime)
    : null;

  if (now < secondaryStart) return "awaiting_window";
  if (secondaryEnd && now > secondaryEnd) return "missed";
  return "window_open";
}
